import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACC_ROWS = [
  { id: 'appartement-1', label: "L'Appartement Terrasse", short: 'Apt Terrasse' },
  { id: 'appartement-2', label: "L'Appartement Panorama", short: 'Apt Panorama' },
  { id: 'gite-seul', label: 'Gite seul', short: 'Gite seul' },
  { id: 'gite-salle', label: 'Gite + Salle des fetes', short: 'Gite + Salle' },
  { id: 'salle-seule', label: 'Salle des fetes seule', short: 'Salle seule' },
];

// Same cross-blocking matrix as backend (server.py CROSS_BLOCKS).
// Booking ANY formula blocks ALL the others.
const CROSS_BLOCKS = {
  'appartement-1': ['gite-seul', 'gite-salle', 'salle-seule'],
  'appartement-2': ['gite-seul', 'gite-salle', 'salle-seule'],
  'gite-seul': ['gite-salle', 'salle-seule', 'appartement-1', 'appartement-2'],
  'gite-salle': ['gite-seul', 'salle-seule', 'appartement-1', 'appartement-2'],
  'salle-seule': ['gite-seul', 'gite-salle', 'appartement-1', 'appartement-2'],
};

const ACC_SHORT = Object.fromEntries(ACC_ROWS.map(r => [r.id, r.short]));

const SOURCE_COLORS = {
  'Site direct': '#7A8B76',
  'Airbnb': '#E07A6A',
  'Booking': '#3B82F6',
  'Externe': '#9CA3AF',
};

const STATUS_STYLES = {
  confirmed: 'bg-[#7A8B76] text-white',
  pending: 'bg-[#CFA97D] text-[#1A2B22]',
  cancelled: 'bg-red-400/60 text-white line-through',
};

const STATUS_LABELS = {
  confirmed: 'Confirmee',
  pending: 'En attente',
  cancelled: 'Annulee',
  imported: 'Importee',
};

const EVENT_LABELS = {
  mariage: 'Mariage',
  seminaire_jour: 'Seminaire jour',
  seminaire_hebergement: 'Seminaire',
  anniversaire_salle: 'Anniv. salle',
  anniversaire_gite_salle: 'Anniv. gite + salle',
};

const parseDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const monthFR = ['janvier','fevrier','mars','avril','mai','juin','juillet','aout','septembre','octobre','novembre','decembre'];

export default function AdminPlanningTimeline({ onOpenReservation }) {
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [span, setSpan] = useState('month'); // 'month' | '60' | '90'
  const [filterSource, setFilterSource] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [reservations, setReservations] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Compute date range
  const { rangeStart, rangeDays } = useMemo(() => {
    if (span === 'month') {
      const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      return { rangeStart: start, rangeDays: end.getDate() };
    }
    const days = Number(span);
    return { rangeStart: new Date(anchor), rangeDays: days };
  }, [anchor, span]);

  const rangeEnd = useMemo(() => {
    const d = new Date(rangeStart);
    d.setDate(d.getDate() + rangeDays - 1);
    return d;
  }, [rangeStart, rangeDays]);

  // Build day list
  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < rangeDays; i++) {
      const d = new Date(rangeStart);
      d.setDate(d.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [rangeStart, rangeDays]);

  const todayISO = toISO(new Date());

  // Fetch data
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('admin_token');
        const headers = { Authorization: `Bearer ${token}` };
        const [resRes, availRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/reservations-unified`, { headers }),
          axios.get(`${BACKEND_URL}/api/availability`),
        ]);
        setReservations(resRes.data || []);
        setBlockedDates(availRes.data || []);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Build PER-DAY state for each formule (rule: 1 single state per cell).
  // Priority: reservation > manual_block > unavailable (cross-block) > free.
  // Then merge consecutive days sharing the same state+payload into "segments".
  const rowSegments = useMemo(() => {
    const out = Object.fromEntries(ACC_ROWS.map(r => [r.id, []]));

    // Reservations: dedupe by id + apply filters + skip cancelled
    const seenIds = new Set();
    const activeRes = [];
    for (const r of reservations) {
      if (r.id && seenIds.has(r.id)) continue;
      if (r.id) seenIds.add(r.id);
      if (r.status === 'cancelled') continue;
      if (filterSource !== 'all' && r.source !== filterSource) continue;
      if (filterStatus !== 'all' && r.status !== filterStatus) continue;
      activeRes.push(r);
    }

    // Airbnb mirrors each booking across all linked listing calendars
    // (e.g. a single Apt1 booking creates an iCal entry on the Apt1 *and* on the parent "Gite seul" calendar).
    // Same for a "Gite seul" booking → it mirrors on Apt1 AND Apt2.
    // We dedupe these mirrored reflections according to the domain's CROSS_BLOCKS rules:
    //   • {Apt1, Apt2, gite-seul} same dates → keep ONLY gite-seul (= single client privatized both apts).
    //   • {Apt1, gite-seul} without Apt2 → keep ONLY Apt1 (= Apt1 reservation reflected on gite-seul).
    //   • {Apt2, gite-seul} without Apt1 → keep ONLY Apt2 (= Apt2 reservation reflected on gite-seul).
    //   • {Apt1, Apt2} without gite-seul → keep BOTH (= 2 independent clients).
    // We also drop iCal reflections that match an Amenitiz/direct reservation already in DB (source of truth).
    const isIcal = (r) => r && r.entry_type === 'ical_import';
    const directRes = activeRes.filter(r => !isIcal(r));
    const icalRes = activeRes.filter(r => isIcal(r));

    // 1) Drop iCal entries that already match a direct/Amenitiz reservation overlapping the same date range and accommodation
    //    (handles the "Lionel Moreau Amenitiz 26/06→03/07 vs iCal gite-seul 26/06→03/07" duplication).
    const intervalsOverlap = (a, b) => a.arrival_date < b.departure_date && b.arrival_date < a.departure_date;
    const icalAfterAmenitizDrop = icalRes.filter(ic => {
      return !directRes.some(d =>
        d.accommodation_type === ic.accommodation_type &&
        intervalsOverlap(d, ic)
      );
    });

    // 2) Group remaining iCal entries by (arrival_date, departure_date) and apply Airbnb mirror rules
    const icalByInterval = {};
    for (const ic of icalAfterAmenitizDrop) {
      const k = `${ic.arrival_date}__${ic.departure_date}`;
      (icalByInterval[k] = icalByInterval[k] || []).push(ic);
    }
    const icalDedupedFinal = [];
    for (const group of Object.values(icalByInterval)) {
      const accs = new Set(group.map(g => g.accommodation_type));
      const hasApt1 = accs.has('appartement-1');
      const hasApt2 = accs.has('appartement-2');
      const hasGite = accs.has('gite-seul');
      let keepAccs;
      if (hasApt1 && hasApt2 && hasGite) {
        keepAccs = new Set(['gite-seul']);
      } else if (hasApt1 && hasGite && !hasApt2) {
        keepAccs = new Set(['appartement-1']);
      } else if (hasApt2 && hasGite && !hasApt1) {
        keepAccs = new Set(['appartement-2']);
      } else {
        keepAccs = accs; // {Apt1, Apt2} without gite-seul = 2 independent bookings → keep both
      }
      for (const ic of group) if (keepAccs.has(ic.accommodation_type)) icalDedupedFinal.push(ic);
    }

    const finalActiveRes = [...directRes, ...icalDedupedFinal];

    // Manual blocks: only those NOT generated by the system AND without a reservation_id
    const SYSTEM_REASONS = new Set(['reservation', 'ical_sync', 'airbnb_sync', 'booking_sync', 'amenitiz_import']);
    const manualBlocks = blockedDates.filter(b => !SYSTEM_REASONS.has(b.reason) && !b.reservation_id);

    for (const acc of Object.keys(out)) {
      // Pre-filter relevant items for this row
      const ownRes = finalActiveRes.filter(r => r.accommodation_type === acc);
      const ownBlocks = manualBlocks.filter(b => b.accommodation_type === acc);
      const crossSources = finalActiveRes.filter(r => r.accommodation_type !== acc && (CROSS_BLOCKS[r.accommodation_type] || []).includes(acc));
      // Safety net: raw sync/cross blocks from DB (covers echoes hidden from the unified list)
      const systemBlocks = blockedDates.filter(b => (b.reason === 'ical_sync' || b.reason === 'cross_block') && b.accommodation_type === acc);

      // For each day, compute state
      const dayStates = days.map((d, dayIdx) => {
        const iso = toISO(d);
        // Priority 1: own reservation
        const r = ownRes.find(x => iso >= x.arrival_date && iso < x.departure_date);
        if (r) return { iso, dayIdx, state: 'reservation', payload: r };
        // Priority 2: manual block (end_date inclusive in DB)
        const mb = ownBlocks.find(b => iso >= b.start_date && iso <= b.end_date);
        if (mb) return { iso, dayIdx, state: 'manual_block', payload: mb };
        // Priority 3: cross-blocked by another reservation
        const sources = crossSources.filter(x => iso >= x.arrival_date && iso < x.departure_date);
        if (sources.length > 0) return { iso, dayIdx, state: 'unavailable', payload: sources };
        // Priority 4: blocked in DB by an iCal sync/cross block (echo or hidden closure)
        const sb = systemBlocks.find(b => iso >= b.start_date && iso < b.end_date);
        if (sb) return { iso, dayIdx, state: 'unavailable', payload: [] };
        return { iso, dayIdx, state: 'free', payload: null };
      });

      // Merge consecutive days with same (state, payload identity)
      const segments = [];
      let cur = null;
      const idOf = (p) => p ? (p.id || (Array.isArray(p) ? p.map(x => x.id).sort().join('|') : '')) : '';
      for (const ds of dayStates) {
        const mergeKey = ds.state + '__' + idOf(ds.payload);
        if (cur && cur.mergeKey === mergeKey) {
          cur.endIdx = ds.dayIdx;
        } else {
          if (cur && cur.state !== 'free') segments.push(cur);
          cur = { mergeKey, state: ds.state, payload: ds.payload, startIdx: ds.dayIdx, endIdx: ds.dayIdx };
        }
      }
      if (cur && cur.state !== 'free') segments.push(cur);
      out[acc] = segments;
    }
    return out;
  }, [reservations, blockedDates, days, filterSource, filterStatus]);

    // 2. For each reservation placed in step 1, add a "Bloque" band on the cross-blocked rows
  // Compute bar position/size (in day units)
  const positionBar = (startISO, endISO) => {
    const start = parseDate(startISO);
    const end = parseDate(endISO);
    if (!start || !end) return null;
    const dayMs = 86400000;
    const startOffset = Math.max(0, Math.round((start - rangeStart) / dayMs));
    const endOffset = Math.min(rangeDays, Math.round((end - rangeStart) / dayMs));
    const width = Math.max(0.5, endOffset - startOffset);
    return { left: startOffset, width };
  };

  const navigate = (delta) => {
    if (span === 'month') {
      const d = new Date(anchor);
      d.setMonth(d.getMonth() + delta);
      d.setDate(1);
      setAnchor(d);
    } else {
      const days = Number(span);
      const d = new Date(anchor);
      d.setDate(d.getDate() + delta * Math.floor(days / 2));
      setAnchor(d);
    }
  };

  const goToday = () => {
    const d = new Date();
    if (span === 'month') d.setDate(1);
    setAnchor(d);
  };

  const headerLabel = useMemo(() => {
    if (span === 'month') return `${monthFR[anchor.getMonth()]} ${anchor.getFullYear()}`;
    const endLabel = `${rangeEnd.getDate()} ${monthFR[rangeEnd.getMonth()]} ${rangeEnd.getFullYear()}`;
    const startLabel = `${rangeStart.getDate()} ${monthFR[rangeStart.getMonth()]} ${rangeStart.getFullYear()}`;
    return `${startLabel} → ${endLabel}`;
  }, [span, anchor, rangeStart, rangeEnd]);

  // Day column width: try to fit in available width but maintain minimum readability
  // We use grid-template-columns based on rangeDays for crisp alignment
  const minColWidth = rangeDays > 60 ? 18 : rangeDays > 30 ? 28 : 42;

  return (
    <div className="bg-white rounded-xl p-4 md:p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="planning-timeline">
      {/* Header / controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-[#7A8B76]" strokeWidth={1.5} />
          <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#1A2B22] font-semibold tracking-tight">
            Planning
          </h3>
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="outline" size="sm" className="h-8 px-2.5" onClick={() => navigate(-1)} data-testid="planning-prev">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={goToday} data-testid="planning-today">
            Aujourd&apos;hui
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-2.5" onClick={() => navigate(1)} data-testid="planning-next">
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
        <div className="font-medium text-sm text-[#1A2B22] capitalize min-w-[180px]" data-testid="planning-period-label">
          {headerLabel}
        </div>
        <Select value={span} onValueChange={setSpan}>
          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="planning-span">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">1 mois</SelectItem>
            <SelectItem value="60">2 mois</SelectItem>
            <SelectItem value="90">3 mois</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="planning-filter-source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes sources</SelectItem>
            <SelectItem value="Site direct">Site direct</SelectItem>
            <SelectItem value="Airbnb">Airbnb</SelectItem>
            <SelectItem value="Booking">Booking</SelectItem>
            <SelectItem value="Externe">Externe</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="planning-filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="confirmed">Confirmees</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="cancelled">Annulees</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-[#595959] mb-3">
        <span className="font-medium uppercase tracking-wider text-[10px] text-[#1A2B22]/60">Legende :</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#7A8B76]" /> Reservee confirmee</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#CFA97D]" /> En attente</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: '#E5E2D9', border: '1px solid #C9C5B8' }} /> Indisponible (clic pour la cause)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#E5E2D9]" style={{ backgroundImage: 'repeating-linear-gradient(45deg,#9CA3AF 0,#9CA3AF 2px,transparent 2px,transparent 4px)', border: '1px solid #9CA3AF' }} /> Blocage manuel (Travaux)</span>
        <span className="inline-flex items-center gap-1.5 ml-2"><span className="w-2 h-3 rounded-sm" style={{ background: SOURCE_COLORS['Airbnb'] }} /> Airbnb</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-3 rounded-sm" style={{ background: SOURCE_COLORS['Booking'] }} /> Booking</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-3 rounded-sm" style={{ background: SOURCE_COLORS['Site direct'] }} /> Site direct</span>
      </div>

      {/* Timeline */}
      <div className="overflow-x-auto" data-testid="planning-scroll-wrap">
        <div className="min-w-fit">
          {/* Day header */}
          <div className="flex sticky top-0 z-20 bg-white">
            <div className="w-[130px] md:w-[160px] flex-shrink-0 border-b border-r border-[#E5E2D9] py-2 px-3 text-xs font-semibold uppercase tracking-wider text-[#1A2B22]/60">
              Formule
            </div>
            <div className="flex flex-1 border-b border-[#E5E2D9]">
              {days.map((d, i) => {
                const iso = toISO(d);
                const isToday = iso === todayISO;
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                const isFirst = d.getDate() === 1;
                return (
                  <div
                    key={iso}
                    className={`flex-shrink-0 text-center border-r border-[#E5E2D9]/50 py-1 ${isWeekend ? 'bg-[#F9F8F6]' : ''} ${isToday ? 'bg-[#CFA97D]/20' : ''}`}
                    style={{ width: minColWidth }}
                    title={d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  >
                    <div className={`text-[10px] uppercase ${isToday ? 'text-[#1A2B22] font-bold' : 'text-[#1A2B22]/50'}`}>
                      {['di','lu','ma','me','je','ve','sa'][d.getDay()]}
                    </div>
                    <div className={`text-[11px] ${isToday ? 'text-[#1A2B22] font-bold' : isFirst ? 'text-[#7A8B76] font-semibold' : 'text-[#1A2B22]/70'}`}>
                      {d.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rows */}
          {ACC_ROWS.map(row => {
            const segments = rowSegments[row.id] || [];
            const ROW_H = 48; // single lane only
            return (
              <div key={row.id} className="flex border-b border-[#E5E2D9]/60 last:border-b-0" data-testid={`planning-row-${row.id}`}>
                <div className="w-[130px] md:w-[160px] flex-shrink-0 border-r border-[#E5E2D9] py-3 px-3 text-sm text-[#1A2B22] font-medium bg-[#F9F8F6]/40 flex items-center">
                  {row.short}
                </div>
                <div className="relative flex-1" style={{ minHeight: ROW_H }}>
                  {/* Day cells (background) */}
                  <div className="flex absolute inset-0">
                    {days.map((d) => {
                      const iso = toISO(d);
                      const isToday = iso === todayISO;
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      return (
                        <div
                          key={iso}
                          className={`flex-shrink-0 border-r border-[#E5E2D9]/40 ${isWeekend ? 'bg-[#F9F8F6]' : ''} ${isToday ? 'bg-[#CFA97D]/10' : ''}`}
                          style={{ width: minColWidth }}
                        />
                      );
                    })}
                  </div>
                  {/* Single-state bars (positioned absolutely) */}
                  <div className="relative" style={{ minHeight: ROW_H }}>
                    {segments.map((seg, idx) => {
                      const left = seg.startIdx * minColWidth;
                      const width = (seg.endIdx - seg.startIdx + 1) * minColWidth - 2;
                      const top = 8;
                      const segId = `${row.id}-${seg.startIdx}-${seg.endIdx}`;

                      // --- 1a. External closure segment (iCal 'Not available' — not a booking) ---
                      if (seg.state === 'reservation' && seg.payload.ical_kind === 'closure') {
                        const rc = seg.payload;
                        const closureColor = SOURCE_COLORS[rc.source] || SOURCE_COLORS['Externe'];
                        return (
                          <div
                            key={`cl-${segId}-${idx}`}
                            data-testid={`planning-closure-${rc.id}`}
                            className="absolute h-8 rounded border border-gray-400 flex items-center gap-1 px-1.5 text-[10px] font-medium text-gray-700 truncate"
                            style={{ left, width, top, borderLeft: `3px solid ${closureColor}`, backgroundImage: 'repeating-linear-gradient(45deg,#9CA3AF33 0,#9CA3AF33 4px,#F9F8F6 4px,#F9F8F6 8px)' }}
                            title={`${rc.name} (${rc.arrival_date} → ${rc.departure_date}) — periode fermee sur ${rc.source}, pas une reservation nominative`}
                          >
                            <Lock className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={2} />
                            <span className="truncate">Indispo. {rc.source}</span>
                          </div>
                        );
                      }

                      // --- 1. Reservation segment ---
                      if (seg.state === 'reservation') {
                        const r = seg.payload;
                        const sourceColor = SOURCE_COLORS[r.source] || SOURCE_COLORS['Externe'];
                        const statusCls = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
                        const evLabel = r.event_type ? EVENT_LABELS[r.event_type] || r.event_type : null;
                        return (
                          <Popover key={`r-${segId}-${idx}`}>
                            <PopoverTrigger asChild>
                              <button
                                data-testid={`planning-bar-${r.id}`}
                                className={`absolute h-8 rounded-md shadow-sm hover:shadow-md hover:scale-[1.02] transition-all flex items-center pl-2 pr-2 text-[11px] font-semibold truncate ${statusCls}`}
                                style={{ left, width, top, borderLeft: `3px solid ${sourceColor}` }}
                                title={`${r.name} — ${r.arrival_date} → ${r.departure_date}`}
                              >
                                <span className="truncate">{r.name}{evLabel ? ` · ${evLabel}` : ''}</span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72" align="start">
                              <div className="space-y-2">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-[#1A2B22]">{r.name}</p>
                                    <p className="text-xs text-[#595959]">{r.email}</p>
                                  </div>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusCls}`}>{STATUS_LABELS[r.status] || r.status}</span>
                                </div>
                                <div className="border-t border-[#E5E2D9] pt-2 text-xs space-y-1">
                                  <p><span className="text-[#595959]">Formule :</span> <span className="font-medium">{row.label}</span></p>
                                  {evLabel && <p><span className="text-[#595959]">Evenement :</span> <span className="font-medium">{evLabel}</span></p>}
                                  <p><span className="text-[#595959]">Dates :</span> <span className="font-medium">{r.arrival_date} → {r.departure_date}</span></p>
                                  <p><span className="text-[#595959]">Source :</span> <span className="font-medium" style={{ color: sourceColor }}>{r.source}</span></p>
                                  {r.amount > 0 && <p><span className="text-[#595959]">Montant :</span> <span className="font-medium">{r.amount} EUR</span></p>}
                                </div>
                                {onOpenReservation && r.entry_type !== 'ical_import' && (
                                  <button type="button" onClick={() => onOpenReservation(r)} data-testid={`planning-open-reservation-${r.id}`} className="w-full mt-2 text-xs font-medium text-[#7A8B76] hover:text-[#1A2B22] underline underline-offset-2 text-left">
                                    Voir la fiche complete →
                                  </button>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        );
                      }

                      // --- 2. Manual block segment ---
                      if (seg.state === 'manual_block') {
                        const b = seg.payload;
                        const reasonLabel = b.reason && b.reason !== 'manual' ? b.reason : 'Travaux';
                        return (
                          <div
                            key={`mb-${segId}-${idx}`}
                            data-testid={`planning-block-${b.id}`}
                            className="absolute h-8 rounded border border-gray-400 flex items-center gap-1 px-1.5 text-[10px] font-medium text-gray-700 truncate"
                            style={{ left, width, top, backgroundImage: 'repeating-linear-gradient(45deg,#9CA3AF33 0,#9CA3AF33 4px,#F9F8F6 4px,#F9F8F6 8px)' }}
                            title={`${reasonLabel} (${b.start_date} → ${b.end_date})`}
                          >
                            <Lock className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={2} />
                            <span className="truncate">{reasonLabel}</span>
                          </div>
                        );
                      }

                      // --- 3. Unavailable segment (cross-block, 1 unique badge per range) ---
                      if (seg.state === 'unavailable') {
                        const sources = seg.payload; // array of source reservations
                        return (
                          <Popover key={`u-${segId}-${idx}`}>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                data-testid={`planning-unavail-${segId}`}
                                className="absolute h-8 rounded-md flex items-center justify-center px-2 text-[10px] font-medium truncate hover:bg-[#E5E2D9] transition-all"
                                style={{ left, width, top, background: '#E5E2D9', color: '#5A6153', border: '1px solid #C9C5B8' }}
                                title="Indisponible (clic pour voir la cause)"
                              >
                                <span className="truncate">Indisponible</span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80" align="start">
                              <div className="space-y-2">
                                <p className="text-sm font-semibold text-[#1A2B22]">Indisponible — cause</p>
                                <p className="text-xs text-[#595959]">
                                  {sources.length > 0
                                    ? 'Cette formule est bloquee car une autre formule du domaine est reservee.'
                                    : 'Periode bloquee par une synchronisation externe (Airbnb / Booking).'}
                                </p>
                                <div className="border-t border-[#E5E2D9] pt-2 space-y-1.5 max-h-64 overflow-y-auto">
                                  {sources.map((s, si) => {
                                    const sc = SOURCE_COLORS[s.source] || SOURCE_COLORS['Externe'];
                                    const accLabel = ACC_SHORT[s.accommodation_type] || s.accommodation_type;
                                    return (
                                      <button
                                        key={s.id || si}
                                        type="button"
                                        onClick={() => onOpenReservation && s.entry_type !== 'ical_import' && onOpenReservation(s)}
                                        data-testid={`planning-unavail-source-${si}`}
                                        disabled={!onOpenReservation || s.entry_type === 'ical_import'}
                                        className="w-full text-left p-2 rounded-md hover:bg-[#F9F8F6] disabled:cursor-default disabled:hover:bg-transparent transition-colors border border-transparent hover:border-[#E5E2D9]"
                                        style={{ borderLeft: `3px solid ${sc}` }}
                                      >
                                        <p className="text-xs font-semibold text-[#1A2B22] truncate">Bloque par reservation {accLabel}</p>
                                        <p className="text-[10px] text-[#595959] truncate">
                                          <span className="font-medium">{s.name || '—'}</span>
                                          {' · '}
                                          <span style={{ color: sc }}>{s.source}</span>
                                          {' · '}
                                          {s.arrival_date} → {s.departure_date}
                                        </p>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        );
                      }
                      return null;
                    })}
                    {segments.length === 0 && !loading && (
                      <div className="flex items-center justify-center h-full text-[10px] text-[#1A2B22]/30 italic">
                        Aucune reservation
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="text-center py-6 text-sm text-[#595959]">Chargement du planning...</div>
      )}
    </div>
  );
}
